import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CheckSquare, Info, Loader2, Lock, Newspaper, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime, humanize } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Chip, ConfidenceBadge, DemoBadge, EmptyState, fmtWait, Loading, PageHeader, Panel, QueryState, RiskBadge, SourceBadge, StatusBadge } from "@/components/common";

const METRICS = [["impressions", "Impressions"], ["clicks", "Clicks"], ["leads", "Leads"], ["qualified_leads", "Qualified leads"], ["spend", "Spend"]];

function Section({ n, title, tone, tag, testId, children }) {
  return (
    <section data-testid={testId} className="cq-card p-5 sm:p-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="font-mono text-[11px]" style={{ color: tone }}>0{n}</span>
        <h2 className="font-display text-lg text-[#F7FAFF]">{title}</h2>
        {tag}
      </div>
      {children}
    </section>
  );
}

function Measured({ m, currency }) {
  return (
    <Section n={1} title="Measured results" tone="#22E6F2" testId="briefing-measured"
      tag={<>{m.is_demo ? <DemoBadge testId="briefing-measured-demo" /> : <SourceBadge source={m.source} testId="briefing-measured-source" />}<span className="text-xs text-[#7C8BAD]">Recorded figures · last 7 days vs prior 7</span></>}>
      {!m.has_data ? <p className="text-sm text-[#AAB6CC]">No results were measured this week. Import a platform report in Analytics, or connect a channel, to measure performance.</p> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {METRICS.filter(([k]) => m.totals[k]).map(([k, l]) => { const t = m.totals[k]; const up = (t.change_pct ?? 0) >= 0; return (
            <div key={k} className="p-4 rounded-lg bg-[#050816]/60 border border-[#172342]"><div className="text-xs text-[#AAB6CC]">{l}{k === "spend" && ` (${currency})`}</div>
              <div className="font-display text-xl text-[#F7FAFF] mt-1 tabular-nums">{Math.round(t.value).toLocaleString()}</div>
              {t.change_pct !== null && <div className={`text-[11px] font-mono mt-1 flex items-center gap-0.5 ${up ? "text-[#4BE3A4]" : "text-[#FF7792]"}`}>{up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}{t.change_pct}%</div>}
            </div>); })}
        </div>
        {m.by_channel.length > 0 && (
          <div className="mt-4 overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-xs text-[#7C8BAD]"><th className="text-left py-2 font-medium">Channel</th><th className="text-right font-medium">Leads</th><th className="text-right font-medium">Qualified</th><th className="text-right font-medium">Spend</th></tr></thead>
            <tbody>{m.by_channel.map((c) => <tr key={c.channel} className="border-t border-[#172342] text-[#D5DDEB]"><td className="py-2">{c.channel}</td><td className="text-right tabular-nums">{c.leads ?? "—"}</td><td className="text-right tabular-nums">{c.qualified_leads ?? "—"}</td><td className="text-right tabular-nums">{c.spend === undefined ? "—" : Math.round(c.spend).toLocaleString()}</td></tr>)}</tbody></table></div>)}
      </>)}
    </Section>
  );
}

function Approvals({ items }) {
  return (
    <Section n={2} title="Requires your approval" tone="#F4B860" testId="briefing-approvals" tag={<Chip tone="amber">{items.length} waiting</Chip>}>
      {!items.length ? <p className="text-sm text-[#AAB6CC]">Nothing is waiting for a decision.</p> : (
        <div className="divide-y divide-[#172342]">{items.map((a) => (
          <Link key={a.id} to={`/approvals?id=${a.id}`} data-testid={`briefing-approval-${a.id}`} className="py-3 flex flex-wrap items-center gap-2 hover:text-[#22E6F2]">
            <span className="flex-1 min-w-0 text-sm text-[#F7FAFF]">{a.item_title}<span className="block text-xs text-[#7C8BAD]">{a.requested_action} · waiting {a.age_days}d{a.is_demo ? " · demo" : ""}</span></span>
            <RiskBadge level={a.risk_level} /><ConfidenceBadge level={a.confidence_level} />
          </Link>))}</div>)}
    </Section>
  );
}

function Overdue({ items }) {
  if (!items) return null;
  return (
    <Section n={3} title="Overdue approvals" tone="#FF7792" testId="briefing-overdue" tag={<Chip tone={items.length ? "red" : "green"}>{items.length} overdue</Chip>}>
      {!items.length ? <p className="text-sm text-[#AAB6CC]">Nothing is past its review window.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-xs text-[#7C8BAD]"><th className="text-left py-2 font-medium">Item</th><th className="text-left font-medium">Waiting on</th><th className="text-left font-medium">Risk</th><th className="text-right font-medium">Waiting</th></tr></thead>
          <tbody>{items.map((a) => (
            <tr key={a.id} data-testid={`briefing-overdue-${a.id}`} className="border-t border-[#172342] text-[#D5DDEB]">
              <td className="py-2.5 pr-3"><Link to={`/approvals?id=${a.id}`} className="hover:text-[#22E6F2]">{a.item_title}</Link>{a.is_demo && <span className="ml-1.5 font-mono text-[9.5px] text-[#F4B860]">DEMO</span>}</td>
              <td className={a.assigned ? "text-[#F7FAFF]" : "text-[#F4B860]"}>{a.responsible}</td>
              <td><RiskBadge level={a.risk_level} /></td>
              <td className="text-right font-mono text-xs">{fmtWait(a.waiting_hours)}</td>
            </tr>))}</tbody></table></div>)}
    </Section>
  );
}

function Risks({ items }) {
  return (
    <Section n={4} title="Risks" tone="#FF7792" testId="briefing-risks" tag={<span className="text-xs text-[#7C8BAD]">Rule-based checks on your workspace records</span>}>
      {!items.length ? <p className="text-sm text-[#AAB6CC]">No risks flagged.</p> : (
        <ul className="space-y-2.5">{items.map((r, i) => (
          <li key={i} className="flex gap-3 items-start"><RiskBadge level={r.level} /><div className="text-sm"><div className="text-[#F7FAFF]">{r.title}</div><div className="text-xs text-[#7C8BAD]">{r.detail}</div></div></li>))}</ul>)}
    </Section>
  );
}

function Recommendations({ b }) {
  const { path, can } = useWorkspace();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const save = async (i) => {
    setBusy(i);
    try { await api.post(path(`/briefings/${b.id}/recommendations/${i}/save`)); toast.success("Added to the AI Growth Advisor. Nothing has changed yet."); qc.invalidateQueries({ queryKey: ["ws"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  return (
    <Section n={5} title="AI recommendations" tone="#B89CFF" testId="briefing-recommendations" tag={<Chip tone="violet"><Sparkles size={11} /> AI generated</Chip>}>
      {b.ai_status === "running" ? <Loading label="Analysing this week" /> : b.ai_status === "failed" ? <p className="text-sm text-[#AAB6CC]">The AI analysis was unavailable for this briefing. Measured results, approvals and risks above are complete.</p>
        : !b.ai_recommendations.length ? <p className="text-sm text-[#AAB6CC]">No recommendations this week.</p> : (
          <div className="space-y-3">{b.ai_recommendations.map((r, i) => (
            <div key={i} data-testid={`briefing-rec-${i}`} className="p-4 rounded-lg border border-[#172342] bg-[#050816]/40">
              <div className="flex flex-wrap items-center gap-2"><span className="text-sm font-medium text-[#F7FAFF] flex-1 min-w-0">{r.title}</span><ConfidenceBadge level={r.confidence_level} /><RiskBadge level={r.impact_risk} /></div>
              <p className="mt-2 text-sm text-[#AAB6CC]">{r.rationale}</p>
              <p className="mt-1 text-xs text-[#7C8BAD]"><span className="text-[#AAB6CC]">Evidence:</span> {r.evidence}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                <span className="text-[#D5DDEB]">Suggested: {r.suggested_action}</span>
                <span className="ml-auto" />
                {r.informational_only ? <span data-testid={`briefing-rec-info-${i}`} className="flex items-center gap-1 text-[#7C8BAD]"><Info size={12} /> Informational only (low confidence)</span>
                  : r.saved_recommendation_id ? <Link to="/command-centre" className="text-[#4BE3A4]">Added to Advisor</Link>
                    : can("edit") && <button data-testid={`briefing-rec-save-${i}`} disabled={busy === i} onClick={() => save(i)} className="cq-btn cq-btn-ghost !py-1 !text-xs">{busy === i ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add to Advisor</button>}
              </div>
            </div>))}</div>)}
    </Section>
  );
}

function Detail({ id }) {
  const { current } = useWorkspace();
  const q = useWsQuery(`/briefings/${id}`, { refetchInterval: (query) => (query.state.data?.ai_status === "running" ? 3000 : false) });
  if (q.isLoading) return <Loading label="Loading briefing" />;
  if (!q.data) return null;
  const b = q.data; const s = b.snapshot;
  return (
    <div className="space-y-5" data-testid="briefing-detail">
      <div className="cq-card p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#7C8BAD]"><span className="cq-label">Week of {fmtDate(b.period_start, { dateStyle: "medium" })}</span><StatusBadge status={b.status} /><span>{b.trigger === "schedule" ? "Scheduled Monday briefing" : "Generated on demand"} · {fmtDateTime(b.created_at)}</span>
          {b.emailed_to?.length > 0 && <span>· Emailed to {b.emailed_to.length}</span>}</div>
        <div className="mt-4 flex items-start gap-3"><Sparkles size={16} className="text-[#B89CFF] mt-1 shrink-0" />
          <div><div className="cq-muted-label mb-1">AI summary of the data below</div>
            {b.ai_status === "running" ? <p className="text-sm text-[#AAB6CC] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Writing summary…</p>
              : <p data-testid="briefing-summary" className="text-[15px] text-[#F7FAFF] leading-relaxed">{b.summary || "Summary unavailable; the sections below come directly from your workspace records."}</p>}
            {b.highlights?.length > 0 && <ul className="mt-3 space-y-1 text-sm text-[#AAB6CC] list-disc pl-5">{b.highlights.map((h) => <li key={h}>{h}</li>)}</ul>}
          </div></div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {[["Content drafted this week", s.content.drafted_this_week], ["AI drafts awaiting review", s.content.ai_drafts_awaiting_review], ["Decisions recorded", Object.values(s.decisions_this_week).reduce((a, x) => a + x, 0)], ["Upcoming in 7 days", s.upcoming.length]].map(([l, v]) => (
            <div key={l} className="p-3 rounded-lg border border-[#172342]"><div className="text-[#7C8BAD]">{l}</div><div className="font-display text-lg text-[#F7FAFF]">{v}</div></div>))}
        </div>
      </div>
      <Measured m={s.measured} currency={current?.currency || ""} />
      <Approvals items={s.pending_approvals} />
      <Overdue items={s.overdue_approvals} />
      <Risks items={s.risks} />
      <Recommendations b={b} />
    </div>
  );
}

export default function Briefings() {
  const { path, refresh, can } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const q = useWsQuery("/briefings", { refetchInterval: (query) => (query.state.data?.some((b) => b.ai_status === "running") ? 4000 : false) });
  const [busy, setBusy] = useState(false);
  const sel = params.get("id") || q.data?.[0]?.id;
  useEffect(() => { if (!params.get("id") && q.data?.[0]) setParams({ id: q.data[0].id }, { replace: true }); }, [q.data, params, setParams]);
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(path("/briefings")); setParams({ id: data.id }); refresh(); toast.success("Briefing started. Measured figures are ready; the AI summary follows."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Learn" title="Weekly Growth Briefings" description="Every Monday CRESQEN summarises your workspace: what was measured, what needs your decision, the risks, and AI recommendations."
        actions={can("edit") && <button data-testid="briefing-generate-button" disabled={busy} onClick={generate} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <Newspaper size={14} />} Generate now</button>} />
      <div className="flex gap-3 p-4 mb-6 rounded-xl border border-[#243153] bg-[#0A1024] text-sm text-[#AAB6CC]" data-testid="briefing-safety-note">
        <Lock size={15} className="text-[#22E6F2] shrink-0 mt-0.5" /> Briefings are read-only. They never publish content, change campaigns or budgets, or take external action. Recommendations only reach the Approval Queue when you send them.
      </div>
      <QueryState q={q} empty={<EmptyState icon={Newspaper} title="No briefings yet" description="The first one arrives on Monday morning, or generate one now." />}>
        {(items) => (
          <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            <div className="space-y-2" data-testid="briefing-list">{items.map((b) => (
              <button key={b.id} data-testid={`briefing-item-${b.id}`} onClick={() => setParams({ id: b.id })}
                className={`w-full text-left p-3.5 rounded-xl border transition-colors ${sel === b.id ? "border-[#22E6F2]/50 bg-[#22E6F2]/5" : "border-[#243153] hover:border-[#3A4870]"}`}>
                <div className="text-sm text-[#F7FAFF]">Week of {fmtDate(b.period_start)}</div>
                <div className="text-[11px] text-[#7C8BAD] mt-0.5 flex items-center gap-1.5">{humanize(b.trigger)} · {fmtDateTime(b.created_at)}{b.ai_status === "running" && <Loader2 size={11} className="animate-spin" />}</div>
              </button>))}</div>
            {sel && <Detail id={sel} />}
          </div>)}
      </QueryState>
      <div className="mt-8 text-xs text-[#7C8BAD] flex items-center gap-2"><CheckSquare size={12} /> Decisions are made in Approvals. <AlertTriangle size={12} className="ml-2" /> Low-confidence findings are informational only.</div>
    </div>
  );
}
